import { useCallback, useEffect, useMemo } from 'react';
import cx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { isEmpty } from 'lodash';
import { z } from 'zod';

import {
  getActionFromBoard,
  useBulletin,
} from '@autopass-web/feature-bulletin';
import {
  useClickEvent,
  useInViewedEvent,
} from '@autopass-web/mochi-analytics/event';

import { axiosInstance } from '../../services/client';
import styles from './chyron.module.scss';

import type { Board } from '@autopass-web/feature-bulletin';
import type { EmblaCarouselType } from 'embla-carousel';

const Item = ({
  bgPrimary,
  bgSecondary,
  colorText,
  text,
  onClick,
}: {
  bgPrimary: string;
  bgSecondary: string;
  colorText: string;
  text: string;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={() => {
        onClick();
      }}
      className={cx(styles.bg, styles.itemWrapper)}
      style={
        {
          '--primary-bg': bgPrimary,
          '--secondary-bg': bgSecondary,
        } as React.CSSProperties
      }
    >
      <span style={{ color: colorText }}>{text}</span>
    </div>
  );
};

const paymentMethodSchema = z.object({
  content: z.string(),
  transaction_type: z.array(z.string()),
  background_first: z.string().regex(/#[\dacbdef]{6}/),
  background_second: z.string().regex(/#[\dacbdef]{6}/),
  text_color: z.string().regex(/#[\dacbdef]{6}/),
});

const BOARD_CODE = 'pwa_paymentmethod_page';

export const Chyron = ({
  shouldShow = () => true,
}: {
  shouldShow?: (b: Board<z.infer<typeof paymentMethodSchema>>) => boolean;
}) => {
  const bulletin = useBulletin({
    boardCode: BOARD_CODE,
    clientInstance: axiosInstance,
    attributesSchema: paymentMethodSchema,
  });

  const handleClickEvent = useClickEvent('廣告曝光');
  const handleInViewEvent = useInViewedEvent('廣告曝光');

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, axis: 'y' }, [
    Autoplay(),
  ]);

  const boards = useMemo(
    () => bulletin.data?.filter(shouldShow) ?? [],
    [bulletin.data, shouldShow],
  );

  const logEmblaBoardEvent = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const index = emblaApi.selectedScrollSnap();
      const targetBoard = boards?.[index];

      targetBoard &&
        handleInViewEvent?.(
          {
            廣告id: targetBoard.id,
            版位id: BOARD_CODE,
          },
          { key: targetBoard.id },
        );
    },
    [boards, handleInViewEvent],
  );

  useEffect(() => {
    if (emblaApi) emblaApi.on('slidesInView', logEmblaBoardEvent);
  }, [emblaApi, logEmblaBoardEvent]);

  if (isEmpty(boards)) {
    return null;
  }

  return (
    <div className={styles.viewport} ref={emblaRef}>
      <div className={styles.container}>
        {boards.map((board) => {
          return (
            <Item
              key={board.id}
              bgPrimary={board.attributes.background_first}
              bgSecondary={board.attributes.background_second}
              colorText={board.attributes.text_color}
              text={board.attributes.content}
              onClick={() => {
                const action = getActionFromBoard({
                  board,
                  isAlphaEnv: bulletin.isAlphaEnv,
                });

                handleClickEvent({
                  廣告id: board.id,
                  版位id: BOARD_CODE,
                });

                action();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
